import React, { FunctionComponent, useEffect, useMemo } from 'react'
import { Text } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import isEqual from 'lodash/isEqual'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  removeUnknownLineItem,
  selectLineItems,
  selectUnknownLineItems,
  selectCustomFields,
  selectServiceFields,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { FulfilmentType } from '../../../models/fulfilment'
import { LineItem, UnknownLineItem } from '../../../models/line-item'
import { SaleLineItem } from '../sale-line-item/LineItem'
import { UnknownLineItem as SaleUnknownLineItem } from '../unknown-line-item/UnknownLineItem'
import { useHotkeysService, useSaleManager } from '../../../ng-compat/hooks'
import { EVENT } from '../../../constants'
import { setExpandedLineItemId } from '../../../state/slices/uiStateSlice'
import { hasServiceOrder } from '../../../functions/service'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { useShowSection } from './useShowSection'

const isLineItem = (
  lineItem: LineItem | UnknownLineItem
): lineItem is LineItem => 'fulfilmentType' in lineItem

export const SaleSummary: FunctionComponent = () => {
  const lineItems = useAppSelector(selectLineItems, isEqual)
  const unknownLineItems = useAppSelector(selectUnknownLineItems, isEqual)
  const serviceFields = useAppSelector(selectServiceFields)
  const customFields = useAppSelector(selectCustomFields)
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const hotkeysService = useHotkeysService()
  const showTakeNowBanner = useShowSection('TAKENOW')
  const showPickUpBanner = useShowSection('PICKUP')
  const showDeliveryBanner = useShowSection('DISPATCH')
  const ref = React.useRef<HTMLLIElement>(null)
  const hasShowServicePicklist = getFeatureFromCache('show_service_pick_lists')
  const isServiceSale =
    hasShowServicePicklist && hasServiceOrder(serviceFields, customFields)

  const {
    nonFulfillmentLineItems,
    pickUpLineItems,
    deliveryLineItems,
  } = useMemo(() => {
    return [...lineItems, ...unknownLineItems]
      .sort((a, b) => a.dateAdded - b.dateAdded)
      .reduce<{
        nonFulfillmentLineItems: Array<LineItem | UnknownLineItem>
        pickUpLineItems: LineItem[]
        deliveryLineItems: LineItem[]
      }>(
        (acc, item) => {
          if (!isLineItem(item)) {
            acc.nonFulfillmentLineItems.push(item)
          } else if (item.fulfilmentType === FulfilmentType.PICKUP) {
            acc.pickUpLineItems.push(item)
          } else if (item.fulfilmentType === FulfilmentType.DELIVERY) {
            acc.deliveryLineItems.push(item)
          } else {
            acc.nonFulfillmentLineItems.push(item)
          }
          return acc
        },
        {
          nonFulfillmentLineItems: [],
          pickUpLineItems: [],
          deliveryLineItems: [],
        }
      )
  }, [lineItems, unknownLineItems])

  const lastAddedLineItem = useMemo(() => {
    return [...lineItems, ...unknownLineItems].sort(
      (a, b) => b.dateAdded - a.dateAdded
    )[0]
  }, [lineItems, unknownLineItems])

  useEffect(() => {
    if (!lastAddedLineItem?.localId) {
      return
    }

    const config = {
      action: () => {
        dispatch(setExpandedLineItemId(lastAddedLineItem.localId))
      },
      combo: ['alt+l'],
      summary: 'Toggle last line item',
    }
    hotkeysService.add(config)

    return () => {
      hotkeysService.remove(config.combo)
    }
  }, [dispatch, hotkeysService, lastAddedLineItem])

  const scrollIntoView = () => {
    if (ref.current) {
      ref.current.scrollIntoView()
    }
  }

  useEffect(() => {
    saleManager.on(EVENT.SALE_ADD_LINE_ITEM, scrollIntoView)
    saleManager.on(EVENT.SALE_ADD_UNKNOWN_LINE_ITEM, scrollIntoView)

    return () => {
      saleManager.off(EVENT.SALE_ADD_LINE_ITEM, scrollIntoView)
      saleManager.off(EVENT.SALE_ADD_UNKNOWN_LINE_ITEM, scrollIntoView)
    }
  }, [saleManager])

  const onDeleteUnknownLineItem = (item: UnknownLineItem) => {
    dispatch(removeUnknownLineItem(item.localId))
    saleManager.saveSale()
  }

  return (
    <ul className="sale-list">
      {showDeliveryBanner ? (
        <div>
          <Text type="signpost" className="sale-fulfilment-banner">
            {isServiceSale ? (
              <T
                _str="TO BE ORDERED FOR SERVICE"
                _context="Fulfillment service message banner"
              />
            ) : (
              <T _str="DELIVER LATER" _context="Fulfillment message banner" />
            )}
          </Text>
        </div>
      ) : null}
      {deliveryLineItems.map(lineItem => (
        <li
          className="sale-list-item line-item--separator"
          key={lineItem.localId}
        >
          <SaleLineItem lineItem={lineItem} />
        </li>
      ))}

      {showPickUpBanner ? (
        <div>
          <Text type="signpost" className="sale-fulfilment-banner">
            {isServiceSale ? (
              <T
                _str="TO BE ORDERED FOR SERVICE"
                _context="Fulfillment service message banner"
              />
            ) : (
              <T _str="PICK UP LATER" _context="Fulfillment message banner" />
            )}
          </Text>
        </div>
      ) : null}
      {pickUpLineItems.map(lineItem => (
        <li
          className="sale-list-item line-item--separator"
          key={lineItem.localId}
        >
          <SaleLineItem lineItem={lineItem} />
        </li>
      ))}

      {showTakeNowBanner ? (
        <div>
          <Text type="signpost" className="sale-fulfilment-banner">
            {isServiceSale ? (
              <T
                _str="AVAILABLE FOR SERVICE"
                _context="Fulfillment service message banner"
              />
            ) : (
              <T _str="TAKE NOW" _context="Fulfillment type" />
            )}
          </Text>
        </div>
      ) : null}

      {nonFulfillmentLineItems.map(lineItem => (
        <li
          className="sale-list-item line-item--separator"
          ref={lastAddedLineItem.localId === lineItem.localId ? ref : null}
          key={lineItem.localId}
        >
          {isLineItem(lineItem) ? (
            <SaleLineItem lineItem={lineItem} />
          ) : (
            <SaleUnknownLineItem
              item={lineItem as UnknownLineItem}
              onDelete={onDeleteUnknownLineItem}
            />
          )}
        </li>
      ))}
    </ul>
  )
}
